import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import { useConnectionStore } from "stores/ConnectionStore";

import { FOTS } from "utility/typescript";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";
import ServiceConnectionStep from "components/ServiceConnectionStep";

// Disable this check so we match the backend regex exactly.
// eslint-disable-next-line no-useless-escape
const WEBHOOK_REGEX = /^(?:https:\/\/)(?:[^@\/\n]+@)?(?:www\.)?([^:\/?\n]+)/;

const WebhookConnectSetupPage = memo(function WebhookConnectSetupPage() {
  const [, connectionActions] = useConnectionStore();

  const [webhookUrl, setWebhookUrl] = useState("");
  const [inputError, setInputError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [uuid, setUuid] = useState("");

  const connect = () => {
    setLoading(true);
    connectionActions.webhookConnect({ webhookUrl, onSuccess, onError });
  };

  const reset = () => {
    setWebhookUrl("");
    setInputError(false);
  };

  const onSuccess = (data: any) => {
    setLoading(false);
    toast.success("Successfully connected Webhook", {
      position: "bottom-center",
    });
    connectionActions.loadConnections({ reload: true });
    setComplete(true);
    setUuid(data.uuid);
  };

  const onError = () => {
    setLoading(false);
    toast.error("Something went wrong connecting Webhook", {
      position: "bottom-center",
    });
  };

  return (
    <>
      <Breadcrumbs>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/destinations"
        >
          Environment
        </Breadcrumb>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/destinations"
        >
          Destinations
        </Breadcrumb>
        <span />
      </Breadcrumbs>

      <ServiceConnectionInstructions
        label="Webhook Connection"
        forceCollapse={complete ? new Date() : undefined}
      >
        <Stack spacing={2}>
          <Typography variant="body1" fontWeight="bold">
            How ThreatKey Webhooks work:
          </Typography>
          <Typography variant="body1">
            ThreatKey Webhooks are a way for the ThreatKey platform to
            communicate with a third-party application in near real-time. When
            an event happens in ThreatKey, a webhook is sent to the third-party
            application with information about the event. The third-party
            application can then take action based on the event.
          </Typography>
          <Typography variant="body1">
            You can use webhooks to connect to common security applications,
            such as Splunk, to receive near real-time alerts of security events.
          </Typography>
          <Typography variant="body1">
            ThreatKey Webhooks can also be used with automation platforms like
            Zapier and Tray. Automation platforms can send security alerts to a
            variety of different services, such as Jira, PagerDuty, or email.
          </Typography>
        </Stack>
      </ServiceConnectionInstructions>

      {!complete ? (
        <ServiceConnectionStep disabled={loading}>
          <Typography variant="body1">
            Please paste the <strong>Webhook URL</strong> that you would like to
            connect to.
          </Typography>
          <TextField
            fullWidth
            label="Webhook URL"
            variant="outlined"
            sx={{ mt: 1 }}
            value={webhookUrl}
            onChange={(e: FOTS) => {
              const webhook = e?.target?.value.trim();
              const webhookValid = !(webhook.match(WEBHOOK_REGEX) == null);

              setInputError(!!webhook && !webhookValid);
              setWebhookUrl(webhook);
            }}
            error={inputError}
            helperText={
              inputError ? "Invalid Webhook URL (are you missing HTTPS?)" : ""
            }
            data-private="lipsum"
          />
          <Stack sx={{ mt: 2 }} spacing={2} direction="row">
            <LoadingButton
              variant="contained"
              onClick={connect}
              loading={loading}
              disabled={!webhookUrl || inputError}
            >
              Connect Webhook
            </LoadingButton>
            <Button variant="text" onClick={reset}>
              Clear
            </Button>
          </Stack>
        </ServiceConnectionStep>
      ) : (
        <ServiceConnectionStep disabled={false}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CheckCircleOutlineRoundedIcon color="success" sx={{ mr: 1 }} />
            <Typography variant="body1">Done!</Typography>
          </Box>
          <Stack sx={{ mt: 2 }} spacing={1} direction="column">
            <Typography variant="body1">
              <Link
                to={`/environment/destinations/webhook/${uuid}/notifications`}
              >
                Click here
              </Link>{" "}
              to go to your Webhook notification settings.
            </Typography>
          </Stack>
        </ServiceConnectionStep>
      )}
    </>
  );
});

export default WebhookConnectSetupPage;
